import { inject, Injectable, Type } from '@angular/core';
import { AppDialogParam } from '@service/dialog/app-dialog';
import { AppDialogRef } from '@service/dialog/app-dialog-reference';
import { AppDialogGateway } from '@service/dialog/app-dialog.gateway';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PrimeDialogRef } from './primeng-dialog-ref';

/**
 * Implementación del servicio de diálogo dinámico utilizando PrimeNG.
 * Este servicio se encarga de abrir diálogos modales y gestionar su estado.
 */
@Injectable({
  providedIn: 'root'
})
export class PrimeDynamicDialogService implements AppDialogGateway {
  private ref?: DynamicDialogRef;

  /**
   * Inyección del servicio de diálogo de PrimeNG.
   * @private
   */
  private readonly dialogService = inject(DialogService);

  /**
   * Abre un diálogo modal con el componente y parámetros especificados.
   * @param component Componente a mostrar en el diálogo.
   * @param params Parámetros a pasar al componente del diálogo.
   * @returns Referencia al diálogo abierto.
   */
  show(component: Type<unknown>, params?: AppDialogParam): AppDialogRef {
    this.ref =
      this.dialogService.open(component, {
        data: params,
        focusOnShow: false,
        closeOnEscape: false,
        dismissableMask: false,
        modal: true
      }) ?? undefined;

    return PrimeDialogRef.newInstance(this.ref!);
  }

  /**
   * Devuelve la referencia al diálogo abierto.
   * @returns Referencia al diálogo abierto.
   */
  getRef(): AppDialogRef {
    if (!this.ref) {
      throw new Error('Dialog reference is not initialized.');
    }
    return PrimeDialogRef.newInstance(this.ref);
  }

  /**
   * Devuelve los parámetros del diálogo abierto.
   * @returns parámetros del diálogo abierto.
   */
  getParams(): AppDialogParam {
    if (!this.ref) {
      throw new Error('Dialog reference is not initialized.');
    }
    const instance = this.dialogService.getInstance(this.ref);
    return instance?.data as AppDialogParam;
  }
}
